import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import * as flags from "country-flag-icons/react/3x2";
import { motion } from "framer-motion";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";

interface LanguageItem {
  id?: string;
  code: string;
  name: string;
  nativeName: string;
  countryCode: string;
}

interface LanguageModalProps {
  languages: LanguageItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Helper function to get a flag component by country code
const getFlagComponent = (countryCode: string): ReactNode => {
  const FlagComponent = flags[countryCode as keyof typeof flags];
  return FlagComponent ? (
    <FlagComponent className="w-9 h-6 rounded shadow-sm" />
  ) : null;
};

export function LanguageModal({
  languages,
  open,
  onOpenChange,
}: LanguageModalProps) {
  const t = useTranslations("HomePage");
  const locale = useLocale();

  // Sort languages based on locale
  const sortedLanguages = [...languages];

  if (locale === "ja") {
    // For Japanese locale, prioritize these languages in order of importance for Japanese users
    const priorityLanguages = [
      "japanese",
      "english",
      "chinese-simplified",
      "chinese-traditional",
      "korean",
      "cantonese",
      "thai",
      "vietnamese",
      "indonesian",
      "filipino",
      "french",
      "german",
      "spanish",
      "italian",
      "russian",
    ];

    // First sort all languages alphabetically by name
    sortedLanguages.sort((a, b) => a.name.localeCompare(b.name));

    // Then move priority languages to the top (in reverse order to maintain the desired sequence)
    priorityLanguages.reverse().forEach((langCode) => {
      const index = sortedLanguages.findIndex((lang) => lang.code === langCode);
      if (index !== -1) {
        const [lang] = sortedLanguages.splice(index, 1);
        sortedLanguages.unshift(lang);
      }
    });
  } else {
    // For English locale, sort alphabetically
    sortedLanguages.sort((a, b) => a.name.localeCompare(b.name));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "inset-0 overflow-hidden",
          // Override default positioning for fullscreen
          "fixed left-0 top-0 translate-x-0 translate-y-0 w-full h-full max-w-none max-h-none p-0",
          // Override animations with simple fade
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          "data-[state=open]:duration-300 data-[state=closed]:duration-200",
          // Remove default slide and zoom animations
          "data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100",
          "data-[state=open]:slide-in-from-center data-[state=closed]:slide-out-to-center"
        )}
        style={{ borderRadius: 0 }}
      >
        {/* Fixed header */}
        <div className="fixed top-0 left-0 right-0 z-20 bg-background border-b p-6">
          <DialogHeader className="flex flex-row justify-between items-center">
            <DialogTitle className="text-2xl">
              {t("availableLanguages")}
            </DialogTitle>
            <DialogClose className="absolute right-6 top-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </DialogHeader>
        </div>

        {/* Scrollable content with padding to account for fixed header */}
        <div className="h-full w-full overflow-y-auto pt-24 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {sortedLanguages.map((lang, index) => (
              <motion.div
                key={lang.code}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="flex items-center gap-4 p-5 rounded-xl border border-border bg-menu-paper hover:bg-menu-accent/20 transition-colors"
              >
                <div className="h-10 w-10 flex items-center justify-center">
                  {getFlagComponent(lang.countryCode)}
                </div>
                <div>
                  <p className="font-medium font-display">{lang.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {lang.nativeName}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
